/**
 * PermissionEditor Component
 *
 * A comprehensive permission editor for managing role permissions.
 * Displays permissions grouped by category with toggle switches.
 *
 * @example
 * <PermissionEditor
 *   value={{ users: ['view', 'create'], projects: ['view'] }}
 *   onChange={(permissions) => setPermissions(permissions)}
 *   disabled={false}
 * />
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { ChevronDown, ChevronRight, Check, Shield, Search } from 'lucide-react'
import { Input } from '@components/common'
import './PermissionEditor.scss'

/**
 * Default permission definitions organized by category
 * This mirrors the backend PermissionSeeder data
 */
const PERMISSION_DEFINITIONS = {
  'User Management': [
    { resource: 'users', action: 'view', name: 'View Users', description: 'View user list and details' },
    { resource: 'users', action: 'create', name: 'Create Users', description: 'Create new users' },
    { resource: 'users', action: 'edit', name: 'Edit Users', description: 'Edit user information' },
    { resource: 'users', action: 'delete', name: 'Delete Users', description: 'Delete users' },
    { resource: 'users', action: 'assign_role', name: 'Assign Roles', description: 'Assign roles to users' },
  ],
  'Role Management': [
    { resource: 'roles', action: 'view', name: 'View Roles', description: 'View role list and details' },
    { resource: 'roles', action: 'create', name: 'Create Roles', description: 'Create new roles' },
    { resource: 'roles', action: 'edit', name: 'Edit Roles', description: 'Edit role information' },
    { resource: 'roles', action: 'delete', name: 'Delete Roles', description: 'Delete roles' },
    { resource: 'roles', action: 'assign_permissions', name: 'Assign Permissions', description: 'Assign permissions to roles' },
  ],
  'Project Management': [
    { resource: 'projects', action: 'view', name: 'View Projects', description: 'View project list and details' },
    { resource: 'projects', action: 'create', name: 'Create Projects', description: 'Create new projects' },
    { resource: 'projects', action: 'edit', name: 'Edit Projects', description: 'Edit project information' },
    { resource: 'projects', action: 'delete', name: 'Delete Projects', description: 'Delete projects' },
    { resource: 'projects', action: 'assign', name: 'Assign Projects', description: 'Assign users to projects' },
  ],
  'Compliance': [
    { resource: 'checklists', action: 'view', name: 'View Checklists', description: 'View compliance checklists' },
    { resource: 'checklists', action: 'create', name: 'Create Checklists', description: 'Create new checklists' },
    { resource: 'checklists', action: 'edit', name: 'Edit Checklists', description: 'Edit checklist items' },
    { resource: 'checklists', action: 'delete', name: 'Delete Checklists', description: 'Delete checklists' },
    { resource: 'checklists', action: 'approve', name: 'Approve Checklists', description: 'Approve checklist items' },
    { resource: 'policies', action: 'view', name: 'View Policies', description: 'View compliance policies' },
    { resource: 'policies', action: 'create', name: 'Create Policies', description: 'Create new policies' },
    { resource: 'policies', action: 'edit', name: 'Edit Policies', description: 'Edit policy content' },
    { resource: 'policies', action: 'delete', name: 'Delete Policies', description: 'Delete policies' },
    { resource: 'policies', action: 'publish', name: 'Publish Policies', description: 'Publish policies' },
    { resource: 'audits', action: 'view', name: 'View Audits', description: 'View audit records' },
    { resource: 'audits', action: 'create', name: 'Create Audits', description: 'Create new audits' },
    { resource: 'audits', action: 'edit', name: 'Edit Audits', description: 'Edit audit records' },
    { resource: 'audits', action: 'delete', name: 'Delete Audits', description: 'Delete audits' },
    { resource: 'audits', action: 'complete', name: 'Complete Audits', description: 'Mark audits as complete' },
  ],
  'Documents': [
    { resource: 'documents', action: 'view', name: 'View Documents', description: 'View documents' },
    { resource: 'documents', action: 'upload', name: 'Upload Documents', description: 'Upload new documents' },
    { resource: 'documents', action: 'edit', name: 'Edit Documents', description: 'Edit document metadata' },
    { resource: 'documents', action: 'delete', name: 'Delete Documents', description: 'Delete documents' },
    { resource: 'documents', action: 'download', name: 'Download Documents', description: 'Download documents' },
  ],
  'Reports & Analytics': [
    { resource: 'reports', action: 'view', name: 'View Reports', description: 'View compliance reports' },
    { resource: 'reports', action: 'create', name: 'Create Reports', description: 'Generate new reports' },
    { resource: 'reports', action: 'export', name: 'Export Reports', description: 'Export reports' },
    { resource: 'dashboard', action: 'view', name: 'View Dashboard', description: 'View dashboard' },
    { resource: 'dashboard', action: 'customize', name: 'Customize Dashboard', description: 'Customize dashboard widgets' },
  ],
  'Workflows': [
    { resource: 'workflows', action: 'view', name: 'View Workflows', description: 'View workflows' },
    { resource: 'workflows', action: 'create', name: 'Create Workflows', description: 'Create workflows' },
    { resource: 'workflows', action: 'edit', name: 'Edit Workflows', description: 'Edit workflows' },
    { resource: 'workflows', action: 'delete', name: 'Delete Workflows', description: 'Delete workflows' },
    { resource: 'workflows', action: 'execute', name: 'Execute Workflows', description: 'Execute workflows' },
  ],
  'Settings & Administration': [
    { resource: 'settings', action: 'view', name: 'View Settings', description: 'View system settings' },
    { resource: 'settings', action: 'edit', name: 'Edit Settings', description: 'Edit system settings' },
    { resource: 'audit_logs', action: 'view', name: 'View Audit Logs', description: 'View audit logs' },
    { resource: 'audit_logs', action: 'export', name: 'Export Audit Logs', description: 'Export audit logs' },
    { resource: 'certificates', action: 'view', name: 'View Certificates', description: 'View certificates' },
    { resource: 'certificates', action: 'create', name: 'Create Certificates', description: 'Create certificates' },
    { resource: 'certificates', action: 'edit', name: 'Edit Certificates', description: 'Edit certificates' },
    { resource: 'certificates', action: 'delete', name: 'Delete Certificates', description: 'Delete certificates' },
  ],
}

/**
 * Permission toggle component
 */
const PermissionToggle = ({ permission, isChecked, onChange, disabled }) => {
  return (
    <label className={`permission-toggle ${disabled ? 'permission-toggle--disabled' : ''}`}>
      <input
        type="checkbox"
        checked={isChecked}
        onChange={(e) => onChange(permission, e.target.checked)}
        disabled={disabled}
      />
      <span className="permission-toggle__switch">
        {isChecked && <Check size={12} />}
      </span>
      <span className="permission-toggle__content">
        <span className="permission-toggle__name">{permission.name}</span>
        {permission.description && (
          <span className="permission-toggle__description">{permission.description}</span>
        )}
      </span>
    </label>
  )
}

/**
 * Permission category section
 */
const PermissionCategory = ({
  category,
  permissions,
  selectedPermissions,
  onChange,
  disabled,
  isExpanded,
  onToggleExpand,
}) => {
  // Count selected permissions in this category
  const selectedCount = useMemo(() => {
    return permissions.filter((p) =>
      selectedPermissions[p.resource]?.includes(p.action)
    ).length
  }, [permissions, selectedPermissions])

  // Check if all permissions in category are selected
  const allSelected = selectedCount === permissions.length
  const someSelected = selectedCount > 0 && selectedCount < permissions.length

  // Handle select all for category
  const handleSelectAll = () => {
    const newPermissions = { ...selectedPermissions }

    if (allSelected) {
      // Deselect all in this category
      permissions.forEach((p) => {
        if (newPermissions[p.resource]) {
          newPermissions[p.resource] = newPermissions[p.resource].filter(
            (a) => a !== p.action
          )
          if (newPermissions[p.resource].length === 0) {
            delete newPermissions[p.resource]
          }
        }
      })
    } else {
      // Select all in this category
      permissions.forEach((p) => {
        if (!newPermissions[p.resource]) {
          newPermissions[p.resource] = []
        }
        if (!newPermissions[p.resource].includes(p.action)) {
          newPermissions[p.resource].push(p.action)
        }
      })
    }

    onChange(newPermissions)
  }

  return (
    <div className={`permission-category ${isExpanded ? 'permission-category--expanded' : ''}`}>
      <div className="permission-category__header" onClick={onToggleExpand}>
        <button type="button" className="permission-category__toggle">
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        <Shield size={18} className="permission-category__icon" />
        <span className="permission-category__name">{category}</span>
        <span className="permission-category__count">
          {selectedCount} / {permissions.length}
        </span>
        {!disabled && (
          <button
            type="button"
            className={`permission-category__select-all ${allSelected ? 'permission-category__select-all--active' : ''} ${someSelected ? 'permission-category__select-all--partial' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              handleSelectAll()
            }}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="permission-category__permissions">
          {permissions.map((permission) => (
            <PermissionToggle
              key={`${permission.resource}.${permission.action}`}
              permission={permission}
              isChecked={selectedPermissions[permission.resource]?.includes(permission.action) || false}
              onChange={(p, checked) => {
                const newPermissions = { ...selectedPermissions }
                if (checked) {
                  if (!newPermissions[p.resource]) {
                    newPermissions[p.resource] = []
                  }
                  if (!newPermissions[p.resource].includes(p.action)) {
                    newPermissions[p.resource].push(p.action)
                  }
                } else {
                  if (newPermissions[p.resource]) {
                    newPermissions[p.resource] = newPermissions[p.resource].filter(
                      (a) => a !== p.action
                    )
                    if (newPermissions[p.resource].length === 0) {
                      delete newPermissions[p.resource]
                    }
                  }
                }
                onChange(newPermissions)
              }}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Main PermissionEditor component
 */
const PermissionEditor = ({
  value = {},
  onChange,
  disabled = false,
  permissionDefinitions = PERMISSION_DEFINITIONS,
  showSearch = true,
  expandedByDefault = false,
}) => {
  const [expandedCategories, setExpandedCategories] = useState(
    expandedByDefault ? Object.keys(permissionDefinitions) : []
  )
  const [searchTerm, setSearchTerm] = useState('')

  // Filter permissions based on search
  const filteredDefinitions = useMemo(() => {
    if (!searchTerm.trim()) {
      return permissionDefinitions
    }

    const term = searchTerm.toLowerCase()
    const filtered = {}

    Object.entries(permissionDefinitions).forEach(([category, permissions]) => {
      const matchingPermissions = permissions.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.resource.toLowerCase().includes(term) ||
          p.action.toLowerCase().includes(term) ||
          (p.description && p.description.toLowerCase().includes(term))
      )

      if (matchingPermissions.length > 0) {
        filtered[category] = matchingPermissions
      }
    })

    return filtered
  }, [permissionDefinitions, searchTerm])

  // Auto-expand categories when searching
  useEffect(() => {
    if (searchTerm.trim()) {
      setExpandedCategories(Object.keys(filteredDefinitions))
    }
  }, [searchTerm, filteredDefinitions])

  // Toggle category expansion
  const toggleCategory = useCallback((category) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    )
  }, [])

  // Calculate total selected count
  const totalSelected = useMemo(() => {
    return Object.values(value).reduce((sum, actions) => sum + actions.length, 0)
  }, [value])

  const totalPermissions = useMemo(() => {
    return Object.values(permissionDefinitions).reduce(
      (sum, permissions) => sum + permissions.length,
      0
    )
  }, [permissionDefinitions])

  // Handle select/deselect all
  const handleSelectAll = () => {
    if (totalSelected === totalPermissions) {
      // Deselect all
      onChange({})
    } else {
      // Select all
      const allPermissions = {}
      Object.values(permissionDefinitions).flat().forEach((p) => {
        if (!allPermissions[p.resource]) {
          allPermissions[p.resource] = []
        }
        if (!allPermissions[p.resource].includes(p.action)) {
          allPermissions[p.resource].push(p.action)
        }
      })
      onChange(allPermissions)
    }
  }

  // Expand/collapse all
  const handleExpandAll = () => {
    if (expandedCategories.length === Object.keys(permissionDefinitions).length) {
      setExpandedCategories([])
    } else {
      setExpandedCategories(Object.keys(permissionDefinitions))
    }
  }

  return (
    <div className="permission-editor">
      {/* Header */}
      <div className="permission-editor__header">
        <div className="permission-editor__summary">
          <Shield size={20} />
          <span>
            {totalSelected} of {totalPermissions} permissions selected
          </span>
        </div>
        {!disabled && (
          <div className="permission-editor__actions">
            <button
              type="button"
              className="permission-editor__action"
              onClick={handleExpandAll}
            >
              {expandedCategories.length === Object.keys(permissionDefinitions).length
                ? 'Collapse All'
                : 'Expand All'}
            </button>
            <button
              type="button"
              className="permission-editor__action"
              onClick={handleSelectAll}
            >
              {totalSelected === totalPermissions ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      {showSearch && (
        <div className="permission-editor__search">
          <Input
            placeholder="Search permissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search size={16} />}
          />
        </div>
      )}

      {/* Categories */}
      <div className="permission-editor__categories">
        {Object.keys(filteredDefinitions).length === 0 ? (
          <div className="permission-editor__empty">
            No permissions found matching "{searchTerm}"
          </div>
        ) : (
          Object.entries(filteredDefinitions).map(([category, permissions]) => (
            <PermissionCategory
              key={category}
              category={category}
              permissions={permissions}
              selectedPermissions={value}
              onChange={onChange}
              disabled={disabled}
              isExpanded={expandedCategories.includes(category)}
              onToggleExpand={() => toggleCategory(category)}
            />
          ))
        )}
      </div>
    </div>
  )
}

PermissionEditor.propTypes = {
  /** Current permissions value - format: { resource: ['action1', 'action2'] } */
  value: PropTypes.object,
  /** Change handler called with new permissions object */
  onChange: PropTypes.func.isRequired,
  /** Disable all editing */
  disabled: PropTypes.bool,
  /** Custom permission definitions grouped by category */
  permissionDefinitions: PropTypes.object,
  /** Show search input */
  showSearch: PropTypes.bool,
  /** Expand all categories by default */
  expandedByDefault: PropTypes.bool,
}

export default PermissionEditor
